import { HttpStatusCode } from '@angular/common/http';
import { Component, inject, input, signal } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatDialog } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { NavigationEnd } from '@angular/router';
import { ButtonComponent } from '@components/buttons/button/button.component';
import { IconButtonComponent } from '@components/buttons/icon-button/icon-button.component';
import { CountdownReleaseComponent } from '@components/countdown/countdown-release/countdown-release.component';
import {
  AccessabilityDialogComponent,
  MustBeLoginData,
} from '@components/dialogs/accessability-dialog/accessability-dialog.component';
import { ImageGeneratorComponent } from '@components/image-generator/image-generator.component';
import { ProductCastComponent } from '@components/product/product-cast/product-cast.component';
import { ProductCommentComponent } from '@components/product/product-comment/product-comment.component';
import { ProductInfoDesktopComponent } from '@components/product/product-info-desktop/product-info-desktop.component';
import { ProductInfoMobileComponent } from '@components/product/product-info-mobile/product-info-mobile.component';
import { ProductSeasonComponent } from '@components/product/product-season/product-season.component';
import { ProductTrailersComponent } from '@components/product/product-trailers/product-trailers.component';
import { SheydaRowComponent } from '@components/rows/sheyda-row/sheyda-row.component';
import {
  Episode,
  EpisodeReleaseStatus,
  InterestAction,
  InterestType,
  PlayableTrailer,
  Program,
  ProgramWithSeasons,
} from '@shared/graphql/api.generated';
import { billboardSP } from '@shared/helper/responsive-sizes';
import { SnackbarService, SnackbarType } from '@shared/helper/snackbar.service';
import { EpisodeSeoService } from '@shared/seo/episode.seo.service';
import { SchemaService } from '@shared/seo/schema.service';
import { EpisodeService } from '@shared/services/episode.service';
import { GeneralService } from '@shared/services/general.service';
import { InterestService } from '@shared/services/interest.service';
import { ProgramService } from '@shared/services/program.service';
import { TokenService } from '@shared/services/token.service';
import { Subscription, interval } from 'rxjs';

@Component({
  imports: [
    ImageGeneratorComponent,
    MatIconModule,
    MatButtonModule,
    ProductCastComponent,
    ProductInfoDesktopComponent,
    ProductInfoMobileComponent,
    ProductSeasonComponent,
    SheydaRowComponent,
    ProductCommentComponent,
    ProductTrailersComponent,
    ButtonComponent,
    IconButtonComponent,
    CountdownReleaseComponent,
  ],
  providers: [EpisodeService, InterestService, ProgramService, EpisodeSeoService],
  templateUrl: './episode.component.html',
  styleUrls: ['./episode.component.scss'],
})
export class EpisodeComponent {
  tokenService = inject(TokenService);
  programService = inject(ProgramService);
  generalService = inject(GeneralService);
  private episodeService = inject(EpisodeService);
  private interestService = inject(InterestService);
  private episodeSeoService = inject(EpisodeSeoService);
  private snackbar = inject(SnackbarService);
  private dialog = inject(MatDialog);
  private schemaService = inject(SchemaService);

  episodeUId = input.required<string>();
  episode = signal<Episode | undefined>(undefined);
  program = signal<ProgramWithSeasons | undefined>(undefined);
  episodeTrailers = signal<PlayableTrailer[]>([]);
  selectedInterest = signal<InterestAction | null>(null);
  showDLLsection = signal(false);
  submitInterest = signal(false);
  relatedPrograms = signal<Program[]>([]);
  programHasCopyright = signal(false);
  interestAction = InterestAction;
  releaseStatusEnum = EpisodeReleaseStatus;
  billboardSizes = billboardSP;

  private timer$!: Subscription;

  ngOnChanges() {
    this.getEpisodePage();
  }

  ngOnInit() {
    this.generalService.router.events.subscribe({
      next: (val) => {
        if (val instanceof NavigationEnd) {
          this.destroy();
        }
      },
    });
  }

  getEpisodePage() {
    this.episodeService.getEpisodePage(this.episodeUId()).subscribe({
      next: (res) => {
        if (res.status.statusCode === HttpStatusCode.UnavailableForLegalReasons) {
          this.programHasCopyright.set(true);
        }
        this.episode.set(res.data?.episode);
        this.program.set(res.data?.program);
        this.setSeoTags();
        this.episodeTrailers.set(res.data?.episodeTrailers!);
        if (this.program()!.seasons.length) {
          this.selectedInterest.set(this.episode()!.interestDetails?.interestedByUser!);
          this.getRelatedPrograms();
        } else {
          this.generalService.router.navigateByUrl(`/p/${this.program()!.program.uid}`);
        }
      },
      error: (e) => {
        if (e?.networkError?.statusCode === HttpStatusCode.NotFound) {
          this.generalService.router.navigateByUrl('/error/404');
        }
      },
    });
  }

  setSeoTags() {
    this.episodeSeoService.setSeoTags(this.episode()!, this.program()!);
    this.episodeSeoService.setEpisodeBreadcrumbsJsonLd(this.episode()!, this.program()!);
    this.episodeSeoService.setEpisodeJsonLd(this.episode()!, this.program()!);
    this.schemaService.setWebsiteObjectJsonLd();
  }

  handleLoginAndWatch() {
    this.generalService.router.navigate(['/auth'], {
      queryParams: {
        ru: `/e/${this.episode()!.uid}`,
      },
    });
  }

  getRelatedPrograms() {
    this.programService.getRelatedPrograms(this.program()!.program.id).subscribe({
      next: (res) => {
        this.relatedPrograms.set(res.data);
      },
      error: (e) => {},
    });
  }

  handlePlayLink() {
    if (this.episodeService.checkEpisodeDisplay(this.episode()!)) {
      this.generalService.router.navigateByUrl(`/w/${this.episode()!.uid}`);
    }
  }

  handleHoverOnLikeBtn() {
    this.timer$ = interval(100).subscribe({
      next: (t: number) => {
        if (t === 1) {
          this.showDLLsection.set(true);
          this.timer$.unsubscribe();
        }
      },
    });
  }

  handleShowHoveredElement() {
    this.showDLLsection.set(true);
  }

  handleLeaveHoveredElement() {
    this.showDLLsection.set(false);
  }

  handleLeaveOnLikeBtn() {
    if (this.timer$) {
      this.timer$.unsubscribe();
    }
  }

  handleInterestAction(action: InterestAction) {
    this.submitInterest.set(true);
    this.showDLLsection.set(false);
    if (this.selectedInterest() === action) {
      this.removeInterest();
    } else {
      this.addInterest(action);
    }
  }

  addInterest(action: InterestAction) {
    if (this.tokenService.getRtk()) {
      this.interestService.addInterest(this.episode()!.id, action, InterestType.Episode).subscribe({
        next: (res) => {
          this.submitInterest.set(false);
          this.snackbar.showSnackbar(res.status.message);
          if (res.status.statusCode === HttpStatusCode.Ok) {
            this.selectedInterest.set(action);
            this.showDLLsection.set(false);
          }
        },
        error: (e) => {
          this.submitInterest.set(false);
          this.snackbar.showSnackbar(e.networkError.message, SnackbarType.error);
        },
      });
    } else {
      this.submitInterest.set(false);
      const data: MustBeLoginData = {
        message: 'برای ثبت علاقه‌مندی، ابتدا باید وارد شوید',
      };
      this.dialog.open(AccessabilityDialogComponent, {
        data,
      });
    }
  }

  removeInterest() {
    this.interestService.removeInterest(this.episode()!.id, InterestType.Episode).subscribe({
      next: (res) => {
        this.submitInterest.set(false);
        this.snackbar.showSnackbar(res.status.message);
        if (res.status.statusCode === HttpStatusCode.Ok) {
          this.selectedInterest.set(null);
          this.showDLLsection.set(false);
        }
      },
      error: (e) => {
        this.submitInterest.set(false);
        this.snackbar.showSnackbar(e.networkError.message, SnackbarType.error);
      },
    });
  }

  destroy() {
    if (this.timer$) {
      this.timer$.unsubscribe();
    }
  }

  ngOnDestroy() {
    this.destroy();
  }
}
